import { useThemeContext } from './Provider';
import type { ComponentPresets } from './presets';

export function useTheme() {
  return useThemeContext();
}

/**
 * Hook for accessing component presets with FastGPT aesthetics
 * @returns Component presets for cards, buttons, typography, layouts, and interactive states
 */
export function useComponentPresets(): ComponentPresets {
  const { components } = useTheme();
  return components;
}

/**
 * Hook for accessing layout utilities
 * @returns Layout presets and responsive utilities
 */
export function useLayoutPresets() {
  const { layout, utils } = useTheme();
  return { layout, utils };
}

/**
 * Hook for accessing color utilities
 * @returns Color system and resolution utilities
 */
export function useColorSystem() {
  const { colors, gradient, utils } = useTheme();
  return { colors, gradient, resolveColor: utils.resolveColor };
}

/**
 * Hook for accessing spacing and typography systems
 * @returns Spacing and typography utilities
 */
export function useDesignSystem() {
  const { spacing, typography, shadow } = useTheme();
  return { spacing, typography, shadow };
}

/**
 * Hook for theme mode management
 * @returns Theme mode state and controls
 */
export function useThemeMode() {
  const { mode, setMode, toggleMode, isDark } = useTheme();
  return { mode, setMode, toggleMode, isDark };
}

/**
 * Hook for creating responsive styles with breakpoints
 * @param values - Style values for different breakpoints
 * @returns Responsive style object
 */
export function useResponsiveStyle(values: {
  xs?: React.CSSProperties;
  sm?: React.CSSProperties;
  md?: React.CSSProperties;
  lg?: React.CSSProperties;
  xl?: React.CSSProperties;
}): React.CSSProperties {
  const { token } = useTheme();

  return {
    ...values.xs,
    [`@media (min-width: ${token.screenSM || 576}px)`]: values.sm,
    [`@media (min-width: ${token.screenMD || 768}px)`]: values.md,
    [`@media (min-width: ${token.screenLG || 992}px)`]: values.lg,
    [`@media (min-width: ${token.screenXL || 1200}px)`]: values.xl,
  };
}

/**
 * Hook for creating hover effects with FastGPT aesthetics
 * @param baseStyle - Base styles
 * @param hoverStyle - Hover styles (optional, uses preset if not provided)
 * @returns Style object with hover effects
 */
export function useHoverEffect(
  baseStyle: React.CSSProperties = {},
  _hoverStyle?: React.CSSProperties,
): React.CSSProperties {
  return {
    ...baseStyle,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  };
}

/**
 * Hook for creating focus effects with accessibility compliance
 * @param baseStyle - Base styles
 * @returns Style object with focus effects
 */
export function useFocusEffect(
  baseStyle: React.CSSProperties = {},
): React.CSSProperties {
  return {
    ...baseStyle,
    // Note: CSS-in-JS focus effects should be handled by component state or CSS classes
  };
}

/**
 * Hook for creating interactive button styles
 * @param variant - Button variant
 * @returns Complete button style configuration
 */
export function useButtonStyle(
  variant: keyof ComponentPresets['buttons'] = 'primary',
) {
  const { components } = useTheme();
  return components.buttons[variant]();
}

/**
 * Hook for creating card styles
 * @param variant - Card variant
 * @returns Complete card style configuration
 */
export function useCardStyle(
  variant: keyof ComponentPresets['cards'] = 'default',
) {
  const { components } = useTheme();
  return components.cards[variant]();
}

/**
 * Hook for creating typography styles
 * @param variant - Typography variant
 * @returns Complete typography style configuration
 */
export function useTypographyStyle(
  variant: keyof ComponentPresets['typography'] = 'body',
) {
  const { components } = useTheme();
  return components.typography[variant]();
}

export function useClickEffect(
  baseStyle: React.CSSProperties = {},
): React.CSSProperties {
  return {
    ...baseStyle,
    cursor: 'pointer',
    transition: 'all 0.1s ease',
  };
}
